import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { ApiError, createTeam } from '../api';
import { getFreshBrowserSessionJwt } from '../auth/teamJwt';
import { Wordmark } from '../components/Chrome';
import { consoleConfig } from '../config';
import {
  ALERT_ERROR,
  BUTTON_PRIMARY_LG,
  BUTTON_TEXT,
  CALLBACK_SCREEN,
  CARD,
  EYEBROW,
  FIELD_LABEL,
  FIELD_WIDE,
} from '../controlStyles';
import { orgRootPath } from '../routing';

/** Outside any org, so a user with no team can reach it. */
export const NEW_TEAM_PATH = '/teams/new';

/** Why `POST /api/teams` refused, in words. Shared with the connect page's inline step. */
export function describeCreateTeamFailure(cause: unknown): string {
  return cause instanceof ApiError && cause.status === 429
    ? 'You have created too many teams recently. Try again in an hour.'
    : cause instanceof ApiError && cause.status >= 400 && cause.status < 500
    ? `The team was not created: ${cause.message.replace(/\.?$/, '.')}`
    : 'The team was not created. Try again in a moment.';
}

/**
 * Create a team: a new organization with the signed-in user as its admin and
 * one project. Reached from Switch organization and from the no-team screens.
 */
export function CreateTeamPage() {
  const stytch = useStytchB2BClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const jwt = await getFreshBrowserSessionJwt(stytch.session, consoleConfig.sessionDurationMinutes);
      const created = await createTeam(jwt, trimmed);
      // The workspace reloads its team list when it mounts, so the new org is in it.
      navigate(orgRootPath({ orgId: created.orgId }));
    } catch (cause) {
      console.error('[teams] create failed', cause);
      setError(describeCreateTeamFailure(cause));
      setBusy(false);
    }
  };

  return (
    <main className={CALLBACK_SCREEN}>
      <div className="create-team grid w-[min(30rem,100%)] gap-6">
        <div className="justify-self-center"><Wordmark /></div>
        <form className={`create-team-form ${CARD} grid gap-4 text-left`} onSubmit={(event) => void submit(event)} aria-labelledby="create-team-heading">
          <div className="grid gap-2">
            <span className={EYEBROW}>New team</span>
            <h1 id="create-team-heading" className="m-0 text-[1.45rem] font-semibold leading-tight text-nim">Create a team</h1>
            <p className="m-0 text-[0.88rem] leading-relaxed text-nim-muted">
              You will be its admin, and it starts with one project. Invite people once it exists.
            </p>
          </div>
          <label className="grid gap-1.5">
            <span className={FIELD_LABEL}>Team name</span>
            <input className={FIELD_WIDE} value={name} onChange={(event) => setName(event.target.value)} placeholder="Acme" maxLength={120} autoFocus disabled={busy} />
          </label>
          {error && <div className={ALERT_ERROR} role="alert">{error}</div>}
          <div className="flex items-center gap-3">
            <button className={BUTTON_PRIMARY_LG} type="submit" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create team'}</button>
            <Link className={`${BUTTON_TEXT} no-underline`} to="/">Cancel</Link>
          </div>
        </form>
      </div>
    </main>
  );
}
